import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, PenLine } from "lucide-react";
import { DatePickerField } from "@/components/DatePickerField";

export function SignaturePad({
  title, testKey, signature, onSignature, nameValue, onName, dateValue, onDate,
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(!!signature);

  // Resize the backing store to CSS size (DPR aware) and optionally redraw a snapshot.
  const applySize = (restoreFrom) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    if (restoreFrom) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = restoreFrom;
    }
  };

  useEffect(() => {
    applySize(signature);
    // Only re-setup when the WIDTH actually changes (orientation / layout),
    // NOT on mobile URL-bar show/hide (height-only) — and preserve the drawing.
    const onResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const targetW = Math.round(rect.width * ratio);
      if (targetW === canvas.width) return; // width unchanged → keep drawing intact
      const snapshot = canvas.toDataURL("image/png");
      applySize(snapshot);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch (_) {}
    drawing.current = true;
    const p = getPos(e);
    last.current = p;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
  };

  const end = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch (_) {}
    onSignature(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasInk(false);
    onSignature("");
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <PenLine className="h-4 w-4 text-amber-500" /> {title}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid={`sig-clear-${testKey}`}
          className="h-8 gap-1 text-slate-500">
          <Eraser className="h-3.5 w-3.5" /> Effacer
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid={`sig-canvas-${testKey}`}
        className="sig-canvas h-40 w-full select-none rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800"
        style={{ touchAction: "none" }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
      {!hasInk && <p className="mt-1 text-center text-xs text-slate-400">Signez avec le doigt ou le stylet</p>}
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input value={nameValue ?? ""} onChange={(e) => onName(e.target.value)} placeholder="Nom du signataire"
          data-testid={`sig-name-${testKey}`}
          className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800" />
        <DatePickerField value={dateValue} onChange={onDate} testId={`sig-date-${testKey}`} placeholder="Date de signature" />
      </div>
    </div>
  );
}
